<template>
  <v-container class="bware-leistung-container" fluid>
    <v-card class="leistung-card mb-4">
      <v-card-title class="d-flex align-center flex-wrap ga-2">
        <v-icon color="amber-darken-2" class="mr-2">mdi-counter</v-icon>
        B-Ware Leistung
        <v-spacer></v-spacer>
        <v-chip color="amber-darken-2" variant="flat" size="large">
          {{ dayLabel }}: {{ dayTotal }} Stk.
        </v-chip>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text>
        <v-row class="mb-2" align="center">
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="selectedDate"
              type="date"
              label="Datum"
              variant="outlined"
              density="comfortable"
              hide-details="auto"
              color="amber-darken-2"
              prepend-inner-icon="mdi-calendar"
            />
          </v-col>
          <v-col cols="12" sm="6" md="8">
            <p class="text-medium-emphasis mb-0">
              Tippen = Menge für den gewählten Tag. Alte Tage nachtragen möglich.
            </p>
          </v-col>
        </v-row>

        <v-btn-toggle
          v-model="tapMenge"
          mandatory
          color="amber-darken-2"
          class="mb-4"
          density="comfortable"
        >
          <v-btn :value="1">+1</v-btn>
          <v-btn :value="5">+5</v-btn>
          <v-btn :value="10">+10</v-btn>
        </v-btn-toggle>

        <v-alert
          v-if="missingBWareTyp"
          type="error"
          variant="tonal"
          class="mb-4"
        >
          Retourentyp „B-Ware“ fehlt in den Kategorien. Bitte Admin anlegen.
        </v-alert>

        <v-row v-if="loading.initial" class="py-8" justify="center">
          <v-progress-circular indeterminate color="amber-darken-2" />
        </v-row>

        <v-row v-else dense>
          <v-col
            v-for="typ in geraetetypen"
            :key="typ.id"
            cols="6"
            sm="4"
            md="3"
            lg="2"
          >
            <v-btn
              class="tap-btn"
              block
              height="96"
              color="amber-darken-2"
              variant="tonal"
              :disabled="missingBWareTyp || submitting || !selectedDate"
              @click="tapPlus(typ.id, tapMenge)"
            >
              <div class="tap-btn-content">
                <span class="tap-name">{{ typ.name }}</span>
                <span class="tap-count">{{ countForTyp(typ.id) }}</span>
              </div>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="leistung-card">
      <v-card-title class="d-flex align-center">
        <v-icon color="amber-darken-2" class="mr-2">mdi-format-list-bulleted</v-icon>
        {{ dayLabel }}
        <v-spacer></v-spacer>
        <v-btn
          icon
          variant="text"
          :loading="loading.fetching"
          @click="fetchEntries"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-0">
        <v-table v-if="dayEntries.length" density="comfortable">
          <thead>
            <tr>
              <th>Datum / Zeit</th>
              <th>Gerätetyp</th>
              <th class="text-center">Menge</th>
              <th class="text-center">Aktion</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in dayEntries" :key="item.id">
              <td>{{ formatDateTime(item.erstelldatum) }}</td>
              <td>{{ item.geraetetyp_name || getTypName(item.geraetetyp_id) }}</td>
              <td class="text-center">{{ item.menge }}</td>
              <td class="text-center">
                <v-btn
                  icon
                  size="small"
                  variant="text"
                  color="error"
                  :disabled="submitting"
                  @click="deleteEntry(item.id)"
                >
                  <v-icon size="18">mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-table>
        <div v-else class="pa-6 text-medium-emphasis text-center">
          Keine B-Ware-Leistung für {{ dayLabel }}.
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
/**
 * B-Ware Leistung: Tap-Pad schreibt Retoure-auftraege (retourentyp B-Ware)
 * ohne Auftragsnummer, mit wählbarem Datum (auch Nachtragen).
 */
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import { useAuthStore } from '@/stores/auth';
import { API_BASE_URL } from '../config';
import { useToast } from 'vue-toastification';

const toast = useToast();
const authStore = useAuthStore();

const geraetetypen = ref([]);
const retourentypen = ref([]);
const entries = ref([]);
const submitting = ref(false);
const tapMenge = ref(1);
const selectedDate = ref(toDateInputValue(new Date()));

const loading = ref({
  initial: true,
  fetching: false,
});

const bwareRetourentypId = computed(() => {
  const found = retourentypen.value.find((t) => t.name === 'B-Ware');
  return found ? found.id : null;
});

const missingBWareTyp = computed(() => !loading.value.initial && !bwareRetourentypId.value);

const dayLabel = computed(() => formatDayLabel(selectedDate.value));

const dayEntries = computed(() => {
  const bwareId = bwareRetourentypId.value;
  return entries.value
    .filter(
      (e) =>
        isSameDay(e.erstelldatum, selectedDate.value) &&
        bwareId != null &&
        Number(e.retourentyp_id) === Number(bwareId)
    )
    .sort((a, b) => new Date(b.erstelldatum) - new Date(a.erstelldatum));
});

const dayTotal = computed(() =>
  dayEntries.value.reduce((sum, e) => sum + (parseInt(e.menge, 10) || 0), 0)
);

/**
 * Y-m-d für date-Input.
 */
function toDateInputValue(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Lesbares Tageslabel (Heute / Datum).
 */
function formatDayLabel(ymd) {
  if (!ymd) return '—';
  const today = toDateInputValue(new Date());
  if (ymd === today) return 'Heute';
  try {
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return ymd;
  }
}

/**
 * Prüft, ob erstelldatum zum gewählten Y-m-d gehört.
 */
function isSameDay(dateString, ymd) {
  if (!dateString || !ymd) return false;
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return false;
  return toDateInputValue(d) === ymd;
}

/**
 * Tagesstückzahl für einen Gerätetyp.
 */
function countForTyp(geraetetypId) {
  return dayEntries.value
    .filter((e) => Number(e.geraetetyp_id) === Number(geraetetypId))
    .reduce((sum, e) => sum + (parseInt(e.menge, 10) || 0), 0);
}

/**
 * Liefert den Gerätenamen aus der Typ-Liste.
 */
function getTypName(id) {
  const found = geraetetypen.value.find((item) => item.id === id);
  return found ? found.name : 'Unbekannt';
}

/**
 * Formatiert Datum und Uhrzeit.
 */
function formatDateTime(dateString) {
  if (!dateString) return '—';
  try {
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

/**
 * Lädt Gerätetypen und Retourentypen.
 */
async function fetchTypen() {
  const response = await axios.get(`${API_BASE_URL}admin/categories.php?action=get`, {
    withCredentials: true,
  });
  if (response.data.status !== 'success') {
    throw new Error(response.data.message || 'Kategorien fehlgeschlagen');
  }
  geraetetypen.value = response.data.geraetetypen || [];
  retourentypen.value = response.data.retourentypen || [];
}

/**
 * Lädt Retoure-Aufträge des Users und filtert B-Ware.
 */
async function fetchEntries() {
  if (!authStore.user?.id) return;
  loading.value.fetching = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}auftraege.php`,
      { action: 'get', typ: 'Retoure' },
      { withCredentials: true }
    );
    if (response.data.status === 'success') {
      const bwareId = bwareRetourentypId.value;
      entries.value = (response.data.auftraege || []).filter(
        (a) => bwareId != null && Number(a.retourentyp_id) === Number(bwareId)
      );
    } else {
      toast.error(response.data.message || 'Laden fehlgeschlagen');
    }
  } catch (error) {
    console.error(error);
    toast.error('Verbindungsfehler beim Laden');
  } finally {
    loading.value.fetching = false;
  }
}

/**
 * Speichert eine B-Ware-Leistungsbuchung für selectedDate.
 */
async function tapPlus(geraetetypId, menge) {
  if (!authStore.user?.id) {
    toast.error('Bitte anmelden');
    return;
  }
  if (!bwareRetourentypId.value) {
    toast.error('Retourentyp B-Ware fehlt');
    return;
  }
  if (!selectedDate.value) {
    toast.error('Bitte Datum wählen');
    return;
  }
  if (menge < 1 || menge > 100) {
    toast.error('Menge muss zwischen 1 und 100 liegen');
    return;
  }

  submitting.value = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}auftraege.php`,
      {
        action: 'add',
        typ: 'Retoure',
        geraetetyp_id: geraetetypId,
        retourentyp_id: bwareRetourentypId.value,
        reparaturtyp_id: null,
        auftragsnummer: '',
        menge,
        kommentar: 'B-Ware Leistung',
        erstelldatum: selectedDate.value,
      },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      await fetchEntries();
    } else {
      toast.error(response.data.message || 'Speichern fehlgeschlagen');
    }
  } catch (error) {
    console.error(error);
    toast.error('Verbindungsfehler beim Speichern');
  } finally {
    submitting.value = false;
  }
}

/**
 * Löscht einen Eintrag.
 */
async function deleteEntry(id) {
  submitting.value = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}auftraege.php`,
      { action: 'delete', id },
      { withCredentials: true }
    );
    if (response.data.status === 'success') {
      await fetchEntries();
    } else {
      toast.error(response.data.message || 'Löschen fehlgeschlagen');
    }
  } catch (error) {
    console.error(error);
    toast.error('Verbindungsfehler beim Löschen');
  } finally {
    submitting.value = false;
  }
}

onMounted(async () => {
  try {
    await fetchTypen();
    await fetchEntries();
  } catch (error) {
    console.error(error);
    toast.error('Initialisierung fehlgeschlagen');
  } finally {
    loading.value.initial = false;
  }
});
</script>

<style scoped>
.bware-leistung-container {
  max-width: 1200px;
}

.leistung-card {
  background: rgba(30, 30, 40, 0.6);
}

.tap-btn {
  text-transform: none;
  letter-spacing: normal;
}

.tap-btn-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
}

.tap-name {
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.2;
  white-space: normal;
  text-align: center;
}

.tap-count {
  font-size: 1.4rem;
  font-weight: 700;
}
</style>
